import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  ClusterMetrics,
  PgCluster,
  PgCredentials,
  PgDatabase,
  PgRole,
  QueryResult,
  SchemaGraph,
} from '@internal/plugin-database-common';

export type GrantLevel = 'readonly' | 'readwrite' | 'owner';

/** Frontend surface of the database backend. */
export interface DatabaseApi {
  listClusters(namespace?: string): Promise<PgCluster[]>;
  getCluster(ns: string, name: string): Promise<PgCluster>;
  getCredentials(ns: string, name: string): Promise<PgCredentials>;
  setTtl(ns: string, name: string, minutes: number | null): Promise<PgCluster>;
  getMetrics(ns: string, name: string, rangeMinutes?: number): Promise<ClusterMetrics>;

  listDatabases(ns: string, name: string): Promise<PgDatabase[]>;
  createDatabase(ns: string, name: string, database: string, owner?: string): Promise<void>;
  dropDatabase(ns: string, name: string, database: string, force?: boolean): Promise<void>;
  renameDatabase(ns: string, name: string, database: string, newName: string): Promise<void>;
  importSql(ns: string, name: string, database: string, sql: string): Promise<{ output: string; messages: string[] }>;
  getSchema(ns: string, name: string, database: string): Promise<SchemaGraph>;

  listRoles(ns: string, name: string): Promise<PgRole[]>;
  createRole(ns: string, name: string, role: string, password: string, opts?: { login?: boolean; createDb?: boolean }): Promise<void>;
  dropRole(ns: string, name: string, role: string): Promise<void>;
  setRolePassword(ns: string, name: string, role: string, password: string): Promise<void>;

  grant(ns: string, name: string, database: string, role: string, level: GrantLevel): Promise<void>;
  revoke(ns: string, name: string, database: string, role: string): Promise<void>;

  query(ns: string, name: string, sql: string, database?: string): Promise<QueryResult>;
}

export const databaseApiRef = createApiRef<DatabaseApi>({
  id: 'plugin.database.service',
});

export class DatabaseClient implements DatabaseApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('database');
  }

  /**
   * Performs a request and surfaces the backend's error message.
   *
   * Backstage serialises errors as `{ error: { message } }`; without unwrapping
   * that the UI would only ever show "500 Internal Server Error" instead of the
   * PostgreSQL message that actually explains the failure.
   */
  private async request<T>(
    path: string,
    init?: RequestInit & { raw?: boolean },
  ): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.base()}${path}`, init);

    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) {
          detail = body.error.message;
        }
      } catch {
        // Non-JSON error body; the status line is the best we have.
      }
      throw new Error(detail);
    }

    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  private json(body: unknown): RequestInit {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  private seg(...parts: string[]): string {
    return parts.map(encodeURIComponent).join('/');
  }

  async listClusters(namespace?: string): Promise<PgCluster[]> {
    const q = namespace ? `?namespace=${encodeURIComponent(namespace)}` : '';
    const r = await this.request<{ items: PgCluster[] }>(`/clusters${q}`);
    return r.items;
  }

  async getCluster(ns: string, name: string): Promise<PgCluster> {
    return this.request(`/clusters/${this.seg(ns, name)}`);
  }

  async getCredentials(ns: string, name: string): Promise<PgCredentials> {
    return this.request(`/clusters/${this.seg(ns, name)}/credentials`);
  }

  async setTtl(ns: string, name: string, minutes: number | null): Promise<PgCluster> {
    return this.request(`/clusters/${this.seg(ns, name)}/ttl`, {
      ...this.json({ inactivityMinutes: minutes }),
      method: 'PUT',
    });
  }

  async getMetrics(ns: string, name: string, rangeMinutes = 60): Promise<ClusterMetrics> {
    return this.request(
      `/clusters/${this.seg(ns, name)}/metrics?rangeMinutes=${rangeMinutes}`,
    );
  }

  async listDatabases(ns: string, name: string): Promise<PgDatabase[]> {
    const r = await this.request<{ items: PgDatabase[] }>(
      `/clusters/${this.seg(ns, name)}/databases`,
    );
    return r.items;
  }

  async createDatabase(ns: string, name: string, database: string, owner?: string) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/databases`,
      this.json({ database, owner }),
    );
  }

  async dropDatabase(ns: string, name: string, database: string, force = false) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}?force=${force}`,
      { method: 'DELETE' },
    );
  }

  async renameDatabase(ns: string, name: string, database: string, newName: string) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}/rename`,
      this.json({ newName }),
    );
  }

  async importSql(ns: string, name: string, database: string, sql: string) {
    return this.request<{ output: string; messages: string[] }>(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}/import`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: sql,
      },
    );
  }

  async getSchema(ns: string, name: string, database: string): Promise<SchemaGraph> {
    return this.request(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}/schema`,
    );
  }

  async listRoles(ns: string, name: string): Promise<PgRole[]> {
    const r = await this.request<{ items: PgRole[] }>(
      `/clusters/${this.seg(ns, name)}/roles`,
    );
    return r.items;
  }

  async createRole(
    ns: string,
    name: string,
    role: string,
    password: string,
    opts: { login?: boolean; createDb?: boolean } = {},
  ) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/roles`,
      this.json({ role, password, ...opts }),
    );
  }

  async dropRole(ns: string, name: string, role: string) {
    await this.request(`/clusters/${this.seg(ns, name)}/roles/${this.seg(role)}`, {
      method: 'DELETE',
    });
  }

  async setRolePassword(ns: string, name: string, role: string, password: string) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/roles/${this.seg(role)}/password`,
      this.json({ password }),
    );
  }

  async grant(
    ns: string,
    name: string,
    database: string,
    role: string,
    level: GrantLevel,
  ) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}/grants`,
      this.json({ role, level }),
    );
  }

  async revoke(ns: string, name: string, database: string, role: string) {
    await this.request(
      `/clusters/${this.seg(ns, name)}/databases/${this.seg(database)}/grants/${this.seg(role)}`,
      { method: 'DELETE' },
    );
  }

  async query(ns: string, name: string, sql: string, database = 'postgres') {
    return this.request<QueryResult>(
      `/clusters/${this.seg(ns, name)}/query`,
      this.json({ sql, database }),
    );
  }
}
